/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import { ConlicitasegState } from 'store/modules/conlicitaseg_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import {
  clearFormQuote,
  setFormQuote,
  setContractUploaded,
  setAutofilledFields,
} from 'store/modules/conlicitaseg_store/actions';
import { uploadContractForExtraction } from 'services/actions/conlicitaseg_actions';
import { getInsuranceReasonOptions } from 'services/conlicitaseg';
import InsuranceStepIndicator from 'components/atoms/insurance_step_indicator';
import SquareLoader from 'components/atoms/square_loader';
import QuoteGuaranteeHeader from 'components/atoms/quote_guarantee_header';
import LegalBondNotice from 'components/atoms/legal_bond_notice';
import useCanUploadContract from 'hooks/useCanUploadContract';
import useQuoteFlow from 'hooks/useQuoteFlow';
import { GuaranteeTypeItem, DropZone, FileChip } from './styles';

const guaranteeTypeOptions = getInsuranceReasonOptions().filter(
  (option) => option.value !== 'Licitante',
);

const BiddingQuoteContractUpload: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const inputRef = useRef<HTMLInputElement>(null);

  const { formProcess, request } = useSelector<ApplicationState, ConlicitasegState>(
    (state) => state.session.conlicitaseg,
  );
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [guaranteeType, setGuaranteeType] = useState<string>(formProcess.type_warranty_id || '');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [extractionError, setExtractionError] = useState(false);

  const { config, pathFor, previousPathFor, stepIndicatorProps } = useQuoteFlow();
  const { typeSelectionInUpload } = config;
  const previousPath = previousPathFor('upload');
  const isLoadingFidelizeData = !!request?.get_fidelize_bidding_guarantee?.loading;
  const canContinue = !!guaranteeType && !uploading && !isLoadingFidelizeData;
  const canContinueWithContract = canContinue && !!file;
  const canUploadContract = useCanUploadContract();
  const isBlocked = !!profile?.clientId && !canUploadContract;

  useEffect(() => {
    if (!isBlocked) return;
    if (formProcess.type_warranty_id) history.replace(pathFor('tomador'));
    else if (config.reasonStepTitle) history.replace(pathFor('motivo'));
    else history.replace('/conlicitaseg');
  }, [isBlocked]);

  const handleCancel = () => {
    dispatch(clearFormQuote());
    history.push('/conlicitaseg');
  };

  const handleSelectGuaranteeType = (value: string) => {
    setGuaranteeType(value);
    dispatch(setFormQuote({ type_warranty_id: value }));
  };

  const handleSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExtractionError(false);
    setFile(e.target.files?.[0] || null);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setExtractionError(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const goWithoutContract = () => {
    dispatch(setContractUploaded(false));
    history.push(pathFor('tomador'));
  };

  const handleContinueWithoutContract = () => {
    goWithoutContract();
  };

  const handleContinue = async () => {
    if (!file) return;

    setUploading(true);
    setExtractionError(false);
    try {
      const extracted = await uploadContractForExtraction(file, formProcess.bidding_id);
      if (extracted && Object.keys(extracted).length > 0) {
        dispatch(setFormQuote(extracted));
        dispatch(setAutofilledFields(extracted));
        dispatch(setContractUploaded(true));
        history.push(pathFor('tomador'));
      } else {
        setExtractionError(true);
      }
    } catch {
      setExtractionError(true);
    } finally {
      setUploading(false);
    }
  };

  if (isBlocked) return null;

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="row justify-content-center mt-5">
          <div className="col-12 mx-auto">
            <div className="row align-items-stretch">
              <div className="col-md-9">
                <QuoteGuaranteeHeader title="Seguro Garantia de Execução" />

                <div className="shadow-sm p-4 bg-white rounded">
                  {typeSelectionInUpload && (
                    <>
                      <h3 className="mb-4">Selecione o tipo de garantia</h3>
                      <div className="row">
                        {guaranteeTypeOptions.map((option, idx) => (
                          <div className="col-12 col-md-4 mb-3" key={option.value}>
                            <GuaranteeTypeItem
                              className="form-check px-5 py-3 border rounded bg-white h-100 mb-0"
                              htmlFor={`guarantee-type-${idx}`}
                            >
                              <input
                                className="form-check-input"
                                type="radio"
                                name="guarantee-type"
                                id={`guarantee-type-${idx}`}
                                value={option.value}
                                checked={guaranteeType === option.value}
                                onChange={() => handleSelectGuaranteeType(option.value)}
                              />
                              <div className="form-check-label pl-2">{option.text}</div>
                            </GuaranteeTypeItem>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <h3 className={typeSelectionInUpload ? 'mt-4 mb-3' : 'mb-3'}>
                    Upload do Contrato
                  </h3>
                  <strong className="d-block mb-3">
                    Faça o upload do contrato para aprimorar as informações da sua cotação.
                  </strong>

                  {file && (
                    <div className="d-flex align-items-center mb-3">
                      <FileChip className="d-inline-flex align-items-center mr-3">
                        <i className="material-icons align-middle mr-2" style={{ fontSize: 18 }}>
                          attach_file
                        </i>
                        {file.name}
                      </FileChip>
                      <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        onClick={handleRemoveFile}
                      >
                        Remover
                      </button>
                    </div>
                  )}

                  {extractionError && (
                    <div className="alert alert-warning" role="alert">
                      O contrato anexado não é válido. Faça um novo upload com um contrato válido ou{' '}
                      <a href="/conlicitaseg/contato" target="_blank" rel="noopener noreferrer">
                        entre em contato com nossos especialistas
                      </a>
                      .
                    </div>
                  )}

                  <DropZone
                    type="button"
                    className="btn btn-block text-primary py-5 mb-2"
                    onClick={() => inputRef.current?.click()}
                  >
                    <i className="material-icons align-middle mr-2">upload</i>
                    Clique para selecionar ou arraste os arquivos aqui
                  </DropZone>
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf"
                    className="d-none"
                    onChange={handleSelectFile}
                  />

                  <LegalBondNotice />

                  <div className="d-flex justify-content-end align-items-center mt-4">
                    {previousPath && (
                      <button
                        type="button"
                        className="btn btn-link mr-2"
                        onClick={() => history.push(previousPath)}
                      >
                        Voltar etapa
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-link mr-2"
                      onClick={handleContinueWithoutContract}
                      disabled={!canContinue}
                    >
                      Continuar sem contrato
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleCancel}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ml-2"
                      onClick={handleContinue}
                      disabled={!canContinueWithContract}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        Continuar
                        {uploading && <SquareLoader customClass="conlicitaseg-continue-quote" />}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-3 d-none d-md-block">
                <InsuranceStepIndicator {...stepIndicatorProps('upload')} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiddingQuoteContractUpload;
